// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Input} from '@app/features/ui/components/form/FormInput';
import {unlockWithPassword} from '@app/features/vault/commands/VaultCommands';
import styles from '@app/features/vault/components/UnlockModal.module.css';
import {VAULT_CODE_TEMP, type VaultCode} from '@app/features/vault/constants';
import Vault from '@app/features/vault/state/Vault';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useRef, useState} from 'react';

const UNLOCK_TEMPORARY_DESCRIPTOR = msg({
	message: 'Unlock temporary messages',
	comment: 'Title of the password prompt for the YAPNET!TEMP secret code.',
});
const UNLOCK_HIDDEN_DESCRIPTOR = msg({
	message: 'Unlock hidden conversations',
	comment: 'Title of the password prompt for the YAPNET!HIDDEN secret code.',
});
const WRONG_PASSWORD_DESCRIPTOR = msg({
	message: 'Wrong password, or the data was encrypted with a different one.',
	comment: 'Error shown when a vault password fails to decrypt existing vault data.',
});

interface UnlockModalProps {
	code: VaultCode;
	'data-flx'?: string;
}

export const UnlockModal = observer(({code, 'data-flx': dataFlx}: UnlockModalProps) => {
	const {i18n} = useLingui();
	const [password, setPassword] = useState('');
	const [error, setError] = useState(false);
	const [busy, setBusy] = useState(false);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const title = code === VAULT_CODE_TEMP ? i18n._(UNLOCK_TEMPORARY_DESCRIPTOR) : i18n._(UNLOCK_HIDDEN_DESCRIPTOR);

	const handleUnlock = useCallback(async () => {
		if (busy) return;
		setBusy(true);
		setError(false);
		try {
			const unlocked = await unlockWithPassword(code, password);
			if (unlocked) {
				setPassword('');
				ModalCommands.pop();
				return;
			}
			setError(true);
			inputRef.current?.focus();
		} finally {
			setBusy(false);
		}
	}, [busy, code, password]);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Enter') {
				event.preventDefault();
				void handleUnlock();
			}
		},
		[handleUnlock],
	);

	useEffect(() => {
		// If the modal closes without unlocking (cancel, Escape, backdrop), drop
		// any queued intent so a later unlock does not resurrect it.
		return () => {
			Vault.clearPendingUnlock(code);
		};
	}, [code]);

	return (
		<Modal.Root size="small" centered initialFocusRef={inputRef} data-flx={dataFlx ?? 'vault.unlock-modal.modal-root'}>
			<Modal.Header title={title} data-flx="vault.unlock-modal.modal-header" />
			<Modal.Content data-flx="vault.unlock-modal.modal-content">
				<div className={styles.content} data-flx="vault.unlock-modal.content">
					<p className={styles.description} data-flx="vault.unlock-modal.description">
						<Trans>Enter the password for this vault.</Trans>
					</p>
					<Input
						ref={inputRef}
						type="password"
						autoComplete="off"
						spellCheck={false}
						value={password}
						error={error ? i18n._(WRONG_PASSWORD_DESCRIPTOR) : undefined}
						onChange={(event) => {
							setPassword(event.target.value);
							setError(false);
						}}
						onKeyDown={handleKeyDown}
						placeholder="Password"
						data-flx="vault.unlock-modal.input"
					/>
				</div>
			</Modal.Content>
			<Modal.Footer data-flx="vault.unlock-modal.footer">
				<Button variant="secondary" onClick={() => ModalCommands.pop()} data-flx="vault.unlock-modal.button.cancel">
					<Trans>Cancel</Trans>
				</Button>
				<Button
					variant="primary"
					submitting={busy}
					onClick={() => void handleUnlock()}
					data-flx="vault.unlock-modal.button.unlock"
				>
					<Trans>Unlock</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
